import { Fragment, useState } from 'react';
import { getFieldsByCategory, type ClaimFieldDefinition, createCustomTextInputField, createSignatureField, createStaticTextField, createAdjusterSignatureField, createInitialsField, createLongTextField, createNumberField, createDateField, createCheckboxField } from '@/types/template-fields';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { SignatureCanvas } from '@/components/forms/SignatureCanvas';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

interface FieldPaletteProps {
  onAddCustomField: (field: ClaimFieldDefinition) => void;
  onPickField?: (field: ClaimFieldDefinition) => void;
  enableTapToAdd?: boolean;
  className?: string;
}

type NamedFieldKind = 'text' | 'longText' | 'date' | 'number';
type ContentFieldKind = 'text' | 'signature';

export function FieldPalette({
  onAddCustomField,
  onPickField,
  enableTapToAdd = false,
  className,
}: FieldPaletteProps) {
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(['Insured', 'Claim', 'System', 'Your Content', 'Homeowner Fields']),
  );
  const [isAddFieldDialogOpen, setIsAddFieldDialogOpen] = useState(false);
  const [customFieldLabel, setCustomFieldLabel] = useState('');
  const [namedFieldKind, setNamedFieldKind] = useState<NamedFieldKind>('text');
  const [contentFieldKind, setContentFieldKind] = useState<ContentFieldKind>('text');
  const [contentDraft, setContentDraft] = useState('');
  const [isContentDialogOpen, setIsContentDialogOpen] = useState(false);
  const fieldsByCategory = getFieldsByCategory();

  const handleAddField = (field: ClaimFieldDefinition) => {
    if (enableTapToAdd && onPickField) {
      onPickField(field);
      return;
    }
    onAddCustomField(field);
  };

  const openNamedFieldDialog = (kind: NamedFieldKind) => {
    setNamedFieldKind(kind);
    setIsAddFieldDialogOpen(true);
  };

  const createNamedField = (label: string): ClaimFieldDefinition => {
    switch (namedFieldKind) {
      case 'longText':
        return createLongTextField(label);
      case 'date':
        return createDateField(label);
      case 'number':
        return createNumberField(label);
      default:
        return createCustomTextInputField(label);
    }
  };

  const handleNamedFieldSubmit = () => {
    const label = customFieldLabel.trim();
    if (!label) return;
    handleAddField(createNamedField(label));
    setCustomFieldLabel('');
    setIsAddFieldDialogOpen(false);
  };

  const openContentDialog = (kind: ContentFieldKind) => {
    setContentFieldKind(kind);
    setContentDraft('');
    setIsContentDialogOpen(true);
  };

  const handleTextContentSubmit = () => {
    const content = contentDraft.trim();
    if (!content) return;
    handleAddField(createStaticTextField('Text Block', content));
    setContentDraft('');
    setIsContentDialogOpen(false);
  };

  const toggleCategory = (category: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const categories = Object.entries(fieldsByCategory).filter(([, fields]) => fields.length > 0);

  return (
    <div className={cn("h-full w-64 shrink-0 border-r bg-background flex flex-col", className)}>
      <ScrollArea className="flex-1">
        <div className="p-2 space-y-1">
          {categories.map(([category, fields]) => (
            <Fragment key={category}>
              <div>
              <button
                onClick={() => toggleCategory(category)}
                className="w-full flex items-center gap-1 px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground"
              >
                {expandedCategories.has(category) ? (
                  <ChevronDown className="h-3 w-3" />
                ) : (
                  <ChevronRight className="h-3 w-3" />
                )}
                {category}
                <span className="ml-auto font-normal normal-case">
                  {fields.length}
                </span>
              </button>
              {expandedCategories.has(category) && (
                <div className="mt-1.5 ml-3 space-y-1.5 border-l border-border/70 pl-3">
                  {fields.map((field) => (
                    <button
                      key={field.fieldKey}
                      type="button"
                      onClick={() => handleAddField(field)}
                      className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-left cursor-pointer hover:bg-accent border border-transparent hover:border-border transition-colors group"
                    >
                      <div className="text-foreground truncate text-sm font-medium">
                        {field.label}
                      </div>
                    </button>
                  ))}
                </div>
              )}
              </div>
              {category === 'Claim' && (
                <>
                  <div>
                    <button
                      onClick={() => toggleCategory('Homeowner Fields')}
                      className="w-full flex items-center gap-1 px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground"
                    >
                      {expandedCategories.has('Homeowner Fields') ? (
                        <ChevronDown className="h-3 w-3" />
                      ) : (
                        <ChevronRight className="h-3 w-3" />
                      )}
                      Homeowner Fields
                      <span className="ml-auto font-normal normal-case">7</span>
                    </button>
                    {expandedCategories.has('Homeowner Fields') && (
                      <div className="mt-1.5 ml-3 space-y-1.5 border-l border-border/70 pl-3">
                        <button
                          type="button"
                          onClick={() => openNamedFieldDialog('text')}
                          className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-left cursor-pointer hover:bg-accent border border-transparent hover:border-border transition-colors group"
                        >
                          <div className="text-foreground truncate text-sm font-medium">Single Line Text</div>
                        </button>
                        <button
                          type="button"
                          onClick={() => openNamedFieldDialog('longText')}
                          className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-left cursor-pointer hover:bg-accent border border-transparent hover:border-border transition-colors group"
                        >
                          <div className="text-foreground truncate text-sm font-medium">Multi Line Text</div>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleAddField(createInitialsField())}
                          className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-left cursor-pointer hover:bg-accent border border-transparent hover:border-border transition-colors group"
                        >
                          <div className="text-foreground truncate text-sm font-medium">Initials</div>
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const signatureField = createSignatureField('Homeowner Signature');
                            handleAddField(signatureField);
                          }}
                          className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-left cursor-pointer hover:bg-accent border border-transparent hover:border-border transition-colors group"
                        >
                          <div className="text-foreground truncate text-sm font-medium">Signature</div>
                        </button>
                        <button
                          type="button"
                          onClick={() => openNamedFieldDialog('date')}
                          className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-left cursor-pointer hover:bg-accent border border-transparent hover:border-border transition-colors group"
                        >
                          <div className="text-foreground truncate text-sm font-medium">Date</div>
                        </button>
                        <button
                          type="button"
                          onClick={() => openNamedFieldDialog('number')}
                          className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-left cursor-pointer hover:bg-accent border border-transparent hover:border-border transition-colors group"
                        >
                          <div className="text-foreground truncate text-sm font-medium">Number</div>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleAddField(createCheckboxField())}
                          className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-left cursor-pointer hover:bg-accent border border-transparent hover:border-border transition-colors group"
                        >
                          <div className="text-foreground truncate text-sm font-medium">Checkbox</div>
                        </button>
                      </div>
                    )}
                  </div>
                  <div>
                    <button
                      onClick={() => toggleCategory('Your Content')}
                      className="w-full flex items-center gap-1 px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground"
                    >
                      {expandedCategories.has('Your Content') ? (
                        <ChevronDown className="h-3 w-3" />
                      ) : (
                        <ChevronRight className="h-3 w-3" />
                      )}
                      Your Content
                      <span className="ml-auto font-normal normal-case">2</span>
                    </button>
                    {expandedCategories.has('Your Content') && (
                      <div className="mt-1.5 ml-3 space-y-1.5 border-l border-border/70 pl-3">
                        <button
                          type="button"
                          onClick={() => openContentDialog('text')}
                          className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-left cursor-pointer hover:bg-accent border border-transparent hover:border-border transition-colors group"
                        >
                          <div className="text-foreground truncate text-sm font-medium">Text</div>
                        </button>
                        <button
                          type="button"
                          onClick={() => openContentDialog('signature')}
                          className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-left cursor-pointer hover:bg-accent border border-transparent hover:border-border transition-colors group"
                        >
                          <div className="text-foreground truncate text-sm font-medium">Signature</div>
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </Fragment>
          ))}
        </div>
      </ScrollArea>
      <div className="p-3 border-t space-y-3">
        <p className="text-xs text-muted-foreground">
          {enableTapToAdd
            ? 'Tap a field to place it on the current page.'
            : 'Click a field to place it on the PDF. Use “Your Content” for text and signatures you author.'}
        </p>
      </div>

      {/* Add Custom Field Dialog */}
      <Dialog open={isAddFieldDialogOpen} onOpenChange={setIsAddFieldDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Name {namedFieldKind === 'longText' ? 'Multi Line Text' : namedFieldKind === 'date' ? 'Date' : namedFieldKind === 'number' ? 'Number' : 'Single Line Text'} Field</DialogTitle>
            <DialogDescription>
              Create a text input field that homeowners will fill out when submitting the form.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="field-label">Field Label</Label>
              <Input
                id="field-label"
                placeholder="e.g., Additional Comments, Property Size"
                value={customFieldLabel}
                onChange={(e) => setCustomFieldLabel(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleNamedFieldSubmit();
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setCustomFieldLabel('');
                setIsAddFieldDialogOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                handleNamedFieldSubmit();
              }}
              disabled={!customFieldLabel.trim()}
            >
              Add Field
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isContentDialogOpen} onOpenChange={setIsContentDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{contentFieldKind === 'text' ? 'Add Your Text' : 'Add Your Signature'}</DialogTitle>
            <DialogDescription>
              {contentFieldKind === 'text'
                ? 'Enter the text that should appear on every generated document.'
                : 'Draw the signature that should appear on every generated document.'}
            </DialogDescription>
          </DialogHeader>
          {contentFieldKind === 'text' ? (
            <Textarea
              autoFocus
              value={contentDraft}
              onChange={(event) => setContentDraft(event.target.value)}
              placeholder="Enter your text..."
              className="min-h-32 resize-y"
            />
          ) : (
            <SignatureCanvas
              onSave={(dataUrl) => {
                handleAddField(createAdjusterSignatureField('Your Signature', dataUrl));
                setIsContentDialogOpen(false);
              }}
              onCancel={() => setIsContentDialogOpen(false)}
            />
          )}
          {contentFieldKind === 'text' && (
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsContentDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleTextContentSubmit} disabled={!contentDraft.trim()}>
                Add Text
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
